import { Search, X } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect, useId, useRef, useState } from 'react'
import { cn } from '#lib/utils'

/*
 * Aceternity UI gooey input (ui.aceternity.com/components/gooey-input), adapted:
 * - collapsed, a button sits over the pill; the field is never nested inside a
 *   button (invalid markup, and unreachable for screen readers);
 * - it is open whenever it holds a value (a search from the URL, back/forward
 *   included), without stealing focus;
 * - page surface tokens and the repo's 32px control height, lucide icons;
 * - the original surface sits over the goo so its rounded border and focus
 *   ring are not clipped by the filter's alpha threshold;
 * - a themed clear button replaces the browser's own blue one.
 */

const MotionSearch = motion.create(Search)

const TRANSITION = { duration: 0.4, type: 'spring', bounce: 0.25 } as const

export function GooeyInput(props: {
	placeholder?: string
	/** Short text on the closed pill, where a long placeholder would be cut. */
	collapsedLabel?: string
	clearLabel?: string
	className?: string
	collapsedWidth?: number
	expandedWidth?: number
	expandedOffset?: number
	gooeyBlur?: number
	value: string
	onValueChange: (value: string) => void
	disabled?: boolean
	maxLength?: number
}) {
	const id = useId()
	const filterId = `gooey-filter-${id}`
	const iconLayoutId = `gooey-input-icon-${id}`
	const inputRef = useRef<HTMLInputElement>(null)
	// Opened by hand; a value keeps it open whatever the flag says.
	const [expanded, setExpanded] = useState(false)
	const open = expanded || props.value !== ''
	const wasExpanded = useRef(expanded)

	// Focus only on the collapsed-to-open transition: a pill that mounts open
	// (a search already in the URL) must not grab the page's focus.
	useEffect(() => {
		if (expanded && !wasExpanded.current) {
			inputRef.current?.focus()
		}
		wasExpanded.current = expanded
	}, [expanded])

	const surface = 'bg-background text-foreground shadow-xs ring-1 ring-input'

	return (
		<div className={cn('relative flex items-center', props.className)}>
			<svg aria-hidden='true' className='absolute hidden size-0'>
				<defs>
					<filter
						height='200%'
						id={filterId}
						width='200%'
						x='-50%'
						y='-50%'
					>
						<feGaussianBlur
							in='SourceGraphic'
							result='blur'
							stdDeviation={props.gooeyBlur ?? 4}
						/>
						<feColorMatrix
							in='blur'
							result='goo'
							type='matrix'
							values='1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -10'
						/>
						<feComposite
							in='SourceGraphic'
							in2='goo'
							operator='over'
						/>
					</filter>
				</defs>
			</svg>

			<div
				className='relative flex h-8 items-center'
				style={{ filter: `url(#${filterId})` }}
			>
				<motion.div
					animate={
						open
							? {
									width: props.expandedWidth ?? 260,
									marginLeft: props.expandedOffset ?? 40
								}
							: {
									width: props.collapsedWidth ?? 120,
									marginLeft: 0
								}
					}
					className={cn(
						'relative flex h-8 items-center gap-2 rounded-full px-3 text-sm',
						surface
					)}
					initial={false}
					transition={TRANSITION}
				>
					{open ? null : (
						<MotionSearch
							className='size-4 shrink-0 text-muted-foreground'
							layoutId={iconLayoutId}
						/>
					)}
					{open ? (
						<input
							aria-label={props.placeholder}
							autoComplete='off'
							className='h-full min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:appearance-none'
							disabled={props.disabled}
							enterKeyHint='search'
							maxLength={props.maxLength}
							onBlur={() => setExpanded(false)}
							onChange={(event) =>
								props.onValueChange(event.target.value)
							}
							onFocus={() => setExpanded(true)}
							placeholder={props.placeholder}
							ref={inputRef}
							type='search'
							value={props.value}
						/>
					) : (
						<span className='truncate text-muted-foreground'>
							{props.collapsedLabel ?? props.placeholder}
						</span>
					)}
					{open && props.value !== '' ? (
						<button
							aria-label={props.clearLabel}
							className='-mr-1.5 flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50'
							onClick={() => {
								// Reached by Tab the field has blurred: keep the pill open
								// so focus has somewhere to land.
								setExpanded(true)
								props.onValueChange('')
								inputRef.current?.focus()
							}}
							onMouseDown={(event) => event.preventDefault()}
							type='button'
						>
							<X className='size-3.5' />
						</button>
					) : null}
					{open ? null : (
						<button
							aria-label={
								props.collapsedLabel ?? props.placeholder
							}
							className='absolute inset-0 cursor-pointer rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50'
							disabled={props.disabled}
							onClick={() => setExpanded(true)}
							type='button'
						/>
					)}
				</motion.div>

				<motion.div
					animate={
						open
							? { scale: 1, opacity: 1 }
							: { scale: 0, opacity: 0 }
					}
					aria-hidden='true'
					className='absolute top-1/2 left-0 flex size-8 -translate-y-1/2 items-center justify-center'
					initial={false}
					transition={TRANSITION}
				>
					<div
						className={cn(
							'flex size-8 items-center justify-center rounded-full',
							surface
						)}
					>
						{open ? (
							<MotionSearch
								className='size-4 shrink-0 text-muted-foreground'
								layoutId={iconLayoutId}
							/>
						) : null}
					</div>
				</motion.div>
			</div>
		</div>
	)
}
